import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import { useAuthState } from 'react-firebase-hooks/auth';
import { Table, Input, Button, Tag, Tooltip, Card, Space, Empty } from 'antd';
import { auth } from '../../firebase/firebase-config';
import BeatLoader from "react-spinners/BeatLoader";
import { ConfirmModal } from "../../components";
import UnassignMedicationModal from "./UnassignMedicationModal/UnassignMedicationModal";
import { 
  FileAddOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  ImportOutlined, 
  LinkOutlined,
  DisconnectOutlined,  // Add this import for the unassign icon
  MedicineBoxOutlined,
  SearchOutlined,
  ReloadOutlined 
} from '@ant-design/icons';
import { toast } from "react-toastify";
import { 
  getAllMedications, 
  deleteMedication 
} from "../../services/medications";
import AddMedicationModal from "./AddMedicationModal/AddMedicationModal";
import ImportMedicationsModal from "./ImportMedicationsModal/ImportMedicationsModal";
import AssignMedicationModal from "./AssignMedicationModal/AssignMedicationModal";
import './MedicationPage.css';

const { Search } = Input;

function MedicationsPage() {
  // State management
  const [allMedications, setAllMedications] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [editData, setEditData] = useState(null);
  const [selectedMedicationId, setSelectedMedicationId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentActiveId, setCurrentActiveId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showUnassignModal, setShowUnassignModal] = useState(false);
  
  // Firebase auth
  const [user] = useAuthState(auth);
  
  // Delete medication handlers
  const handleDelete = (id) => {
    setCurrentActiveId(id);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteMedication(currentActiveId);
      toast.success('Medication deleted successfully');
      fetchData();
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Error deleting medication:', error);
      toast.error('Error deleting medication');
    }
  };

  const handleUnassign = (medicationId) => {
    setSelectedMedicationId(medicationId);
    setShowUnassignModal(true);
  };

  
  // Edit medication handler
  const handleEdit = (medicationData) => {
    setEditData(medicationData);
    setShowCreateModal(true);
  };

  // Assign medication handler
  const handleAssign = (medicationId) => {
    setSelectedMedicationId(medicationId);
    setShowAssignModal(true);
  };

  // Modal toggles
  const toggleAddModal = () => {
    setEditData(null);
    setShowCreateModal(!showCreateModal);
  };

  const toggleImportModal = () => {
    setShowImportModal(!showImportModal);
  };

  // Fetch medications data
  const fetchData = async () => {
    setLoading(true);
    setFilteredData([]); // Clear data while loading
    
    try {
      const response = await getAllMedications();
      
      // Handle different response structures
      let medications = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          medications = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          medications = response.data.data;
        }
      }
      
      // Add index property for display
      const medicationsWithIndex = medications.map((med, index) => ({
        ...med,
        index: index + 1
      }));
      
      setAllMedications(medicationsWithIndex);
    } catch (error) {
      console.error('Error fetching medications:', error);
      toast.error('Error fetching medications');
      setAllMedications([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Filter data when medications or search change
  useEffect(() => {
    const dataToFilter = Array.isArray(allMedications) ? allMedications : [];
    const filtered = filterData(dataToFilter, searchText);
    setFilteredData(filtered);
  }, [allMedications, searchText]);

  // Search filter function
  const filterData = (data, query) => {
    if (!Array.isArray(data)) return [];
    if (!query) return data;

    return data.filter((item) =>
      Object.values(item || {}).some((value) =>
        value?.toString().toLowerCase().includes(query.toLowerCase())
      )
    );
  };

  // Search handler
  const handleSearch = (value) => {
    setSearchText(value);
  };

  // Reset filters
  const resetFilters = () => {
    setSearchText('');
  };

  // Get total medication count
  const getMedicationCount = () => {
    return Array.isArray(allMedications) ? allMedications.length : 0;
  };

  // Table columns definition
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => {
        if (!a.name || !b.name) return 0;
        return a.name.localeCompare(b.name);
      },
      render: (text, record) => (
        <div className="medication-name-cell">
          <span className="medication-name">{text || 'No name'}</span>
          {record.nameFr && (
            <span className="medication-name-fr">{record.nameFr}</span>
          )}
        </div>
      ),
    },
    {
      title: 'Presentation',
      dataIndex: 'presentation',
      key: 'presentation',
      render: (text, record) => (
        <div>
          <div>{text || 'N/A'}</div>
          {record.presentationFr && (
            <div className="secondary-text">{record.presentationFr}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      sorter: (a, b) => {
        const priceA = parseFloat(a.price) || 0;
        const priceB = parseFloat(b.price) || 0;
        return priceA - priceB;
      },
      render: (text) => text ? `$${text}` : 'N/A',
    },
    {
      title: 'Therapeutic Class',
      dataIndex: 'therapeuticClass',
      key: 'therapeuticClass',
      render: (text) => text ? <Tag color="#322659">{text}</Tag> : <Tag color="default">N/A</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180, // Increased to accommodate the new button
      render: (_, record) => (
        <div className="action-buttons">
          <Tooltip title="Edit Medication">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)} 
              className="action-button" 
            />
          </Tooltip>
          <Tooltip title="Assign to Facility">
            <Button 
              type="text" 
              icon={<LinkOutlined />} 
              onClick={() => handleAssign(record.id)} 
              className="action-button" 
            />
          </Tooltip>
          <Tooltip title="Unassign from Facility">
            <Button 
              type="text" 
              icon={<DisconnectOutlined />} 
              onClick={() => handleUnassign(record.id)} 
              className="action-button" 
            />
          </Tooltip>
          <Tooltip title="Delete Medication">
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => handleDelete(record.id)} 
              className="action-button" 
            />
          </Tooltip>
        </div>
      ),
    }
  ];

  // Expanded row render function
  const expandedRowRender = (record) => {
    return (
      <div className="expanded-row">
        <div className="expanded-section">
          <h4>Active Molecules and Dosage</h4>
          <p>{record.activeMoleculesAndDosage || 'Not specified'}</p>
          {record.activeMoleculesAndDosageFr && (
            <p className="secondary-text">{record.activeMoleculesAndDosageFr}</p>
          )}
        </div>

        {record.subClass && (
          <div className="expanded-section">
            <h4>Sub-Class</h4>
            <Tag color="#8b5cf6">{record.subClass}</Tag>
            {record.subClassFr && (
              <Tag color="#8b5cf6">{record.subClassFr}</Tag>
            )}
          </div>
        )}

        {record.otherInfo && (
          <div className="expanded-section">
            <h4>Additional Information</h4>
            <p>{record.otherInfo}</p>
            {record.otherInfoFr && (
              <p className="secondary-text">{record.otherInfoFr}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Layout title="Medications" pageTitle="All Medications">
        <div className="section medications-page">
          {/* Summary card */}
          <div className="summary-cards">
            <Card className="summary-card">
              <div className="card-icon">
                <MedicineBoxOutlined />
              </div>
              <div className="card-content">
                <h3>{loading ? <BeatLoader size={8} color="#322659" /> : getMedicationCount()}</h3>
                <p>Total Medications</p>
              </div>
            </Card>
          </div>
          
          {/* Main content */}
          <div className="data-table">
            {/* Header */}
            <div className="top">
              <div className="span">
                <h3>Medications Directory</h3>
              </div>
              <div className="action-group">
                <Button
                  type="primary"
                  icon={<ImportOutlined />}
                  onClick={toggleImportModal}
                  className="btn-import"
                >
                  Import
                </Button>
                <Button
                  type="primary"
                  icon={<FileAddOutlined />}
                  onClick={toggleAddModal}
                  className="btn-add"
                >
                  Add Medication
                </Button>
              </div>
            </div>
            
            {/* Search and filters */}
            <div className="filter-section">
              <Search
                placeholder="Search medications..."
                allowClear
                onSearch={handleSearch}
                onChange={(e) => handleSearch(e.target.value)}
                value={searchText}
                prefix={<SearchOutlined />}
              />
              
              <Space>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={fetchData}
                >
                  Refresh
                </Button>
              </Space>
            </div>
            
            {/* Table */}
            <div className="table-container">
              {loading ? (
                <div className="loading-container">
                  <BeatLoader color="#322659" />
                  <p className="loading-message">Loading medications data...</p>
                </div>
              ) : filteredData.length > 0 ? (
                <Table
                  columns={columns}
                  dataSource={filteredData}
                  rowKey={(record) => record.id || Math.random().toString()}
                  expandable={{
                    expandedRowRender,
                    expandRowByClick: true,
                  }}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} medications`
                  }}
                  className="medications-table"
                />
              ) : (
                <Empty 
                  description={
                    searchText ? "No medications match your search" : "No medications found"
                  } 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </div>
          </div>
        </div>
      </Layout>

      {/* Modals */}
      {showCreateModal && (
        <AddMedicationModal
          editData={editData}
          onClose={() => {
            setShowCreateModal(false);
            setEditData(null);
          }}
          onContentAdded={() => {
            setShowCreateModal(false);
            fetchData();
          }}
        />
      )}

      {showImportModal && (
        <ImportMedicationsModal
          onClose={() => setShowImportModal(false)}
          onImportComplete={() => {
            setShowImportModal(false);
            fetchData();
          }}
        />
      )}

      {showAssignModal && (
        <AssignMedicationModal
          medicationId={selectedMedicationId}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedMedicationId(null);
          }}
          onAssignComplete={() => {
            setShowAssignModal(false);
            toast.success('Medication assigned successfully');
          }}
        />
      )}
      
      {showConfirmModal && (
        <ConfirmModal
          title="Are you sure you want to delete this medication?"
          onAccept={confirmDelete}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}

      {showUnassignModal && (
        <UnassignMedicationModal
          medicationId={selectedMedicationId}
          onClose={() => {
            setShowUnassignModal(false);
            setSelectedMedicationId(null);
          }}
          onUnassignComplete={() => {
            // No need to close the modal here, as the user might want to unassign from multiple facilities
            toast.success('Medication unassigned successfully');
          }}
        />
      )}
    </>
  );
}

export default MedicationsPage;